// src/utils/reporteCategoriasGenerator.js

// 👈 NUEVO: Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// Configuración por defecto (fallback si no hay config del tenant)
export const JEAN_INFORMATICO_CONFIG = {
  nombre: "JEAN INFORMATICO C.A.",
  rif: "J-405678901",
  direccion: "C.C. LATIN CENTER PB LOCAL 01",
  telefono: "0416-7594723",
  whatsapp: "04167594723",
  ciudad: "ACARIGUA - PORTUGUESA",
  email: "jean.informatico@gmail.com",
  sitioWeb: "www.jeaninformatico.com.ve",
  slogan: "Servicio Técnico Especializado",
  lema: "Tecnología al servicio de la gente"
};

/**
 * 👈 NUEVO: Obtiene la config de la empresa (tenant) con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || JEAN_INFORMATICO_CONFIG.nombre,
      rif: datos.rif || JEAN_INFORMATICO_CONFIG.rif,
      direccion: datos.direccion || JEAN_INFORMATICO_CONFIG.direccion,
      telefono: datos.telefono1 || datos.telefono2 || JEAN_INFORMATICO_CONFIG.telefono,
      whatsapp: datos.whatsapp || datos.telefono1 || JEAN_INFORMATICO_CONFIG.whatsapp,
      ciudad: datos.ciudad || JEAN_INFORMATICO_CONFIG.ciudad,
      email: datos.email || JEAN_INFORMATICO_CONFIG.email,
      sitioWeb: datos.sitioWeb || JEAN_INFORMATICO_CONFIG.sitioWeb,
      slogan: datos.slogan || JEAN_INFORMATICO_CONFIG.slogan,
      lema: datos.slogan || JEAN_INFORMATICO_CONFIG.lema,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

/**
 * Formatear fecha para reporte
 */
export const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return fechaISO;
  }
};

/**
 * Formatear fecha solo fecha (sin hora)
 */
export const formatearFechaCorta = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fechaISO;
  }
};

/**
 * Formatear monto/margen
 */
export const formatearPorcentaje = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '0%';
  return `${valor}%`;
};

/**
 * Convertir estado a texto legible
 */
export const convertirEstadoTexto = (estado) => {
  if (!estado) return 'Sin estado';
  const estadoMap = {
    'activo': 'ACTIVA',
    'inactivo': 'INACTIVA',
    'activas': 'ACTIVA',
    'inactivas': 'INACTIVA'
  };
  return estadoMap[estado.toLowerCase()] || estado.toUpperCase();
};

/**
 * Obtener color para estado
 */
export const getColorEstado = (estado) => {
  return estado?.toLowerCase() === 'activo' || estado?.toLowerCase() === 'activas' 
    ? '#10b981' 
    : '#ef4444';
};

/**
 * Calcular estadísticas del reporte
 */
export const calcularEstadisticasReporte = (categorias) => {
  const total = categorias.length;
  const activas = categorias.filter(c => c.estado?.toLowerCase() === 'activo').length;
  const inactivas = categorias.filter(c => c.estado?.toLowerCase() === 'inactivo').length;
  
  const conDescripcion = categorias.filter(c => c.descripcion && c.descripcion.trim() !== '').length;
  
  const categoriasConMargen = categorias.filter(c => c.margen_ganancia_default !== undefined);
  const promedioMargen = categoriasConMargen.length > 0 
    ? Math.round((categoriasConMargen.reduce((sum, c) => sum + (c.margen_ganancia_default || 0), 0) / categoriasConMargen.length) * 10) / 10
    : 0;
  
  const margenes = categorias.map(c => c.margen_ganancia_default || 0).filter(m => m > 0);
  const margenMaximo = margenes.length > 0 ? Math.max(...margenes) : 0;
  const margenMinimo = margenes.length > 0 ? Math.min(...margenes) : 0;
  
  const totalProductos = categorias.reduce((sum, c) => sum + (c.totalProductos || 0), 0);
  
  const fechas = categorias
    .map(c => c.fecha_creacion || c.fechaCreacion)
    .filter(f => f)
    .map(f => new Date(f));
  
  const fechaMasAntigua = fechas.length > 0 
    ? new Date(Math.min(...fechas.map(f => f.getTime())))
    : null;
    
  const fechaMasReciente = fechas.length > 0 
    ? new Date(Math.max(...fechas.map(f => f.getTime())))
    : null;
  
  return {
    total,
    activas,
    inactivas,
    porcentajeActivas: total > 0 ? Math.round((activas / total) * 100) : 0,
    porcentajeInactivas: total > 0 ? Math.round((inactivas / total) * 100) : 0,
    conDescripcion,
    porcentajeConDescripcion: total > 0 ? Math.round((conDescripcion / total) * 100) : 0,
    sinDescripcion: total - conDescripcion,
    promedioMargen,
    margenMaximo,
    margenMinimo,
    totalProductos,
    promedioProductos: total > 0 ? Math.round((totalProductos / total) * 10) / 10 : 0,
    fechaMasAntigua: fechaMasAntigua ? formatearFechaCorta(fechaMasAntigua.toISOString()) : 'N/A',
    fechaMasReciente: fechaMasReciente ? formatearFechaCorta(fechaMasReciente.toISOString()) : 'N/A'
  };
};

/**
 * Generar HTML del reporte de categorías
 */
export const generarHTMLReporteCategorias = (categorias, titulo = 'REPORTE DE CATEGORÍAS', filtros = {}) => {
  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const fechaActual = new Date();
  const estadisticas = calcularEstadisticasReporte(categorias);
  
  const filtrosAplicados = [];
  if (filtros.estado && filtros.estado !== 'all') {
    filtrosAplicados.push(`Estado: ${filtros.estado === 'activas' ? 'ACTIVAS' : 'INACTIVAS'}`);
  }
  if (filtros.busqueda && filtros.busqueda.trim()) {
    filtrosAplicados.push(`Búsqueda: "${filtros.busqueda}"`);
  }
  if (filtros.orden) {
    const ordenMap = {
      'nombre': 'Nombre (A-Z)',
      'nombre_desc': 'Nombre (Z-A)',
      'fechaCreacion_desc': 'Más recientes',
      'fechaCreacion': 'Más antiguas',
      'margen_desc': 'Mayor margen',
      'margen': 'Menor margen'
    };
    filtrosAplicados.push(`Orden: ${ordenMap[filtros.orden] || filtros.orden}`);
  }
  
  const textoFiltros = filtrosAplicados.length > 0 
    ? `Filtros: ${filtrosAplicados.join(' | ')}` 
    : 'Sin filtros aplicados';

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${titulo} - ${empresa.nombre}</title>
      <style>
        @media print {
          @page { margin: 1.5cm; size: letter; }
          body { font-size: 11pt; line-height: 1.3; }
          .no-print { display: none !important; }
          a { text-decoration: none; color: black; }
          .page-break { page-break-after: always; }
        }
        
        body {
          font-family: 'Arial', 'Helvetica', sans-serif;
          margin: 0;
          padding: 20px;
          background: #f5f5f5;
          color: #333;
        }
        
        .reporte-container {
          max-width: 1200px;
          margin: 0 auto;
          background: white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          border-radius: 8px;
          overflow: hidden;
        }
        
        .reporte-header {
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
          color: white;
          padding: 30px 40px;
          text-align: center;
          border-bottom: 4px solid #f59e0b;
        }
        
        .logo-img {
          max-height: 70px;
          max-width: 200px;
          margin-bottom: 12px;
          object-fit: contain;
        }
        
        .logo-text {
          font-size: 28px;
          font-weight: bold;
          margin: 0;
          letter-spacing: 2px;
          color: white;
        }
        
        .slogan {
          font-size: 14px;
          color: #f59e0b;
          margin: 5px 0 0;
          font-style: italic;
        }
        
        .empresa-info {
          margin-top: 15px;
          font-size: 12px;
          color: #ccc;
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 20px;
        }
        
        .empresa-info span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        
        .titulo-reporte {
          text-align: center;
          padding: 20px 40px;
          background: #f8f9fa;
          border-bottom: 2px solid #e9ecef;
        }
        
        .titulo-reporte h1 {
          margin: 0;
          font-size: 24px;
          color: #1a1a1a;
        }
        
        .titulo-reporte p {
          margin: 5px 0 0;
          color: #6c757d;
          font-size: 14px;
        }
        
        .fecha-reporte {
          margin-top: 10px;
          font-size: 12px;
          color: #495057;
        }
        
        .filtros-aplicados {
          background: #e9ecef;
          padding: 10px 40px;
          font-size: 12px;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
        }
        
        .estadisticas-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          padding: 30px 40px;
          background: white;
        }
        
        .estadistica-card {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          border: 1px solid #e9ecef;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        }
        
        .estadistica-valor {
          font-size: 32px;
          font-weight: bold;
          color: #1a1a1a;
          line-height: 1.2;
        }
        
        .estadistica-etiqueta {
          font-size: 12px;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 5px;
        }
        
        .estadistica-detalle {
          font-size: 11px;
          color: #adb5bd;
          margin-top: 5px;
        }
        
        .barras-estado {
          padding: 0 40px 30px;
        }
        
        .barra-container {
          margin-bottom: 15px;
        }
        
        .barra-label {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          font-size: 13px;
        }
        
        .barra-fondo {
          background: #e9ecef;
          height: 24px;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .barra-progreso {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: 10px;
          color: white;
          font-size: 11px;
          font-weight: bold;
        }
        
        .barra-activas {
          background: linear-gradient(90deg, #10b981, #059669);
        }
        
        .barra-inactivas {
          background: linear-gradient(90deg, #ef4444, #dc2626);
        }
        
        .barra-descripcion {
          background: linear-gradient(90deg, #3b82f6, #2563eb);
        }
        
        .tabla-categorias {
          padding: 0 40px 40px;
        }
        
        .tabla-categorias h2 {
          font-size: 18px;
          color: #1a1a1a;
          margin: 0 0 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e9ecef;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        
        th {
          background: #1a1a1a;
          color: white;
          font-weight: 600;
          padding: 12px 8px;
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        td {
          padding: 10px 8px;
          border-bottom: 1px solid #e9ecef;
        }
        
        tr:last-child td {
          border-bottom: none;
        }
        
        tr:hover td {
          background: #f8f9fa;
        }
        
        .estado-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .estado-activo {
          background: #d1fae5;
          color: #065f46;
        }
        
        .estado-inactivo {
          background: #fee2e2;
          color: #991b1b;
        }
        
        .margen-cell {
          font-weight: 600;
          color: #f59e0b;
        }
        
        .resumen-final {
          background: #f8f9fa;
          padding: 30px 40px;
          border-top: 1px solid #e9ecef;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        
        .resumen-item {
          text-align: center;
        }
        
        .resumen-item .label {
          font-size: 11px;
          color: #6c757d;
          text-transform: uppercase;
        }
        
        .resumen-item .valor {
          font-size: 16px;
          font-weight: bold;
          color: #1a1a1a;
          margin-top: 5px;
        }
        
        .reporte-footer {
          background: #1a1a1a;
          color: #999;
          padding: 20px 40px;
          text-align: center;
          font-size: 10px;
          border-top: 4px solid #f59e0b;
        }
        
        .footer-info {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        
        .footer-info span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        
        .copyright {
          margin-top: 10px;
          color: #666;
        }
        
        .controles-impresion {
          padding: 20px 40px;
          text-align: center;
          background: white;
          border-top: 1px solid #e9ecef;
        }
        
        .btn-imprimir {
          background: #1a1a1a;
          color: white;
          border: none;
          padding: 12px 40px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          border: 2px solid #1a1a1a;
        }
        
        .btn-imprimir:hover {
          background: white;
          color: #1a1a1a;
        }
        
        .btn-cerrar {
          background: #6c757d;
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          margin-left: 10px;
          transition: all 0.3s;
        }
        
        .btn-cerrar:hover {
          background: #5a6268;
        }
        
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .font-bold { font-weight: 600; }
        .text-muted { color: #6c757d; }
      </style>
    </head>
    <body>
      <div class="reporte-container">
        <!-- HEADER -->
        <div class="reporte-header">
          ${empresa.logoBase64 ? `<img src="${empresa.logoBase64}" alt="Logo" class="logo-img" />` : ''}
          <div class="logo-text">${empresa.nombre}</div>
          <div class="slogan">${empresa.slogan}</div>
          <div class="empresa-info">
            <span>📞 ${empresa.telefono}</span>
            <span>📱 ${empresa.whatsapp}</span>
            <span>📧 ${empresa.email}</span>
            <span>📍 ${empresa.ciudad}</span>
          </div>
        </div>
        
        <!-- TÍTULO -->
        <div class="titulo-reporte">
          <h1>${titulo}</h1>
          <p>${empresa.lema}</p>
          <div class="fecha-reporte">
            Generado: ${formatearFechaReporte(fechaActual.toISOString())}
          </div>
        </div>
        
        <!-- FILTROS -->
        <div class="filtros-aplicados">
          <strong>📋 ${textoFiltros}</strong>
        </div>
        
        <!-- ESTADÍSTICAS PRINCIPALES -->
        <div class="estadisticas-grid">
          <div class="estadistica-card">
            <div class="estadistica-valor">${estadisticas.total}</div>
            <div class="estadistica-etiqueta">Total Categorías</div>
            <div class="estadistica-detalle">Registradas en sistema</div>
          </div>
          
          <div class="estadistica-card">
            <div class="estadistica-valor">${estadisticas.activas}</div>
            <div class="estadistica-etiqueta">Categorías Activas</div>
            <div class="estadistica-detalle">${estadisticas.porcentajeActivas}% del total</div>
          </div>
          
          <div class="estadistica-card">
            <div class="estadistica-valor">${estadisticas.inactivas}</div>
            <div class="estadistica-etiqueta">Categorías Inactivas</div>
            <div class="estadistica-detalle">${estadisticas.porcentajeInactivas}% del total</div>
          </div>
          
          <div class="estadistica-card">
            <div class="estadistica-valor">${estadisticas.promedioMargen}%</div>
            <div class="estadistica-etiqueta">Margen Promedio</div>
            <div class="estadistica-detalle">Máx: ${estadisticas.margenMaximo}% | Mín: ${estadisticas.margenMinimo}%</div>
          </div>
          
          <div class="estadistica-card">
            <div class="estadistica-valor">${estadisticas.totalProductos}</div>
            <div class="estadistica-etiqueta">Total Productos</div>
            <div class="estadistica-detalle">Prom: ${estadisticas.promedioProductos} por categoría</div>
          </div>
          
          <div class="estadistica-card">
            <div class="estadistica-valor">${estadisticas.conDescripcion}</div>
            <div class="estadistica-etiqueta">Con Descripción</div>
            <div class="estadistica-detalle">${estadisticas.porcentajeConDescripcion}% del total</div>
          </div>
        </div>
        
        <!-- BARRAS DE PROGRESO -->
        <div class="barras-estado">
          <div class="barra-container">
            <div class="barra-label">
              <span>Categorías Activas</span>
              <span>${estadisticas.activas} (${estadisticas.porcentajeActivas}%)</span>
            </div>
            <div class="barra-fondo">
              <div class="barra-progreso barra-activas" style="width: ${estadisticas.porcentajeActivas}%">
                ${estadisticas.porcentajeActivas}%
              </div>
            </div>
          </div>
          
          <div class="barra-container">
            <div class="barra-label">
              <span>Categorías Inactivas</span>
              <span>${estadisticas.inactivas} (${estadisticas.porcentajeInactivas}%)</span>
            </div>
            <div class="barra-fondo">
              <div class="barra-progreso barra-inactivas" style="width: ${estadisticas.porcentajeInactivas}%">
                ${estadisticas.porcentajeInactivas}%
              </div>
            </div>
          </div>
          
          <div class="barra-container">
            <div class="barra-label">
              <span>Con Descripción</span>
              <span>${estadisticas.conDescripcion} (${estadisticas.porcentajeConDescripcion}%)</span>
            </div>
            <div class="barra-fondo">
              <div class="barra-progreso barra-descripcion" style="width: ${estadisticas.porcentajeConDescripcion}%">
                ${estadisticas.porcentajeConDescripcion}%
              </div>
            </div>
          </div>
        </div>
        
        <!-- TABLA DE CATEGORÍAS -->
        <div class="tabla-categorias">
          <h2>📋 Listado Detallado de Categorías</h2>
          
          ${categorias.length > 0 ? `
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Margen</th>
                <th>Productos</th>
                <th>Estado</th>
                <th>Fecha Registro</th>
              </tr>
            </thead>
            <tbody>
              ${categorias.map(cat => `
                <tr>
                  <td>#${cat.idCategoria || ''}</td>
                  <td><strong>${cat.nombre || ''}</strong></td>
                  <td>${cat.descripcion && cat.descripcion.length > 50 
                    ? cat.descripcion.substring(0, 50) + '...' 
                    : (cat.descripcion || '—')}</td>
                  <td class="margen-cell text-right">${formatearPorcentaje(cat.margen_ganancia_default)}</td>
                  <td class="text-center">${cat.totalProductos || 0}</td>
                  <td>
                    <span class="estado-badge ${cat.estado?.toLowerCase() === 'activo' ? 'estado-activo' : 'estado-inactivo'}">
                      ${convertirEstadoTexto(cat.estado || '')}
                    </span>
                  </td>
                  <td>${formatearFechaCorta(cat.fecha_creacion || cat.fechaCreacion)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          ` : `
            <div style="text-align: center; padding: 60px; background: #f8f9fa; border-radius: 8px;">
              <p style="font-size: 16px; color: #6c757d;">No hay categorías para mostrar</p>
            </div>
          `}
        </div>
        
        <!-- RESUMEN FINAL -->
        <div class="resumen-final">
          <div class="resumen-item">
            <div class="label">Categoría más antigua</div>
            <div class="valor">${estadisticas.fechaMasAntigua}</div>
          </div>
          <div class="resumen-item">
            <div class="label">Categoría más reciente</div>
            <div class="valor">${estadisticas.fechaMasReciente}</div>
          </div>
          <div class="resumen-item">
            <div class="label">Rango de márgenes</div>
            <div class="valor">${estadisticas.margenMinimo}% - ${estadisticas.margenMaximo}%</div>
          </div>
        </div>
        
        <!-- FOOTER -->
        <div class="reporte-footer">
          <div class="footer-info">
            <span>🏢 ${empresa.direccion}</span>
            <span>📱 ${empresa.whatsapp}</span>
            <span>🌐 ${empresa.sitioWeb}</span>
          </div>
          <div>RIF ${empresa.rif} | ${empresa.email}</div>
          <div class="copyright">
            © ${new Date().getFullYear()} ${empresa.nombre} - Todos los derechos reservados
          </div>
        </div>
        
        <!-- CONTROLES DE IMPRESIÓN -->
        <div class="controles-impresion no-print">
          <button class="btn-imprimir" onclick="window.print()">
            🖨️ IMPRIMIR REPORTE
          </button>
          <button class="btn-cerrar" onclick="window.close()">
            ✕ CERRAR
          </button>
        </div>
      </div>
      
      <script>
        window.onafterprint = function() {
          // Opcional: cerrar después de imprimir
        };
      </script>
    </body>
    </html>
  `;
};

/**
 * Generar y mostrar reporte de categorías en ventana para imprimir
 */
export const imprimirReporteCategorias = (categorias, titulo = 'REPORTE DE CATEGORÍAS', filtros = {}) => {
  if (!categorias || categorias.length === 0) {
    alert('No hay categorías para mostrar en el reporte');
    return;
  }
  
  const htmlReporte = generarHTMLReporteCategorias(categorias, titulo, filtros);
  
  const ventanaReporte = window.open('', '_blank', 'width=1200,height=800,menubar=yes,toolbar=yes,location=yes,status=yes');
  
  if (!ventanaReporte) {
    alert('Por favor permite ventanas emergentes para generar el reporte');
    return;
  }
  
  ventanaReporte.document.write(htmlReporte);
  ventanaReporte.document.close();
  ventanaReporte.focus();
};

/**
 * Generar reporte simplificado (solo tabla)
 */
export const generarHTMLReporteSimplificado = (categorias, titulo = 'LISTADO DE CATEGORÍAS') => {
  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const fechaActual = new Date();
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${titulo} - ${empresa.nombre}</title>
      <style>
        @media print {
          @page { margin: 1cm; size: letter landscape; }
        }
        
        body {
          font-family: 'Courier New', monospace;
          font-size: 10pt;
          margin: 20px;
        }
        
        h1 {
          text-align: center;
          font-size: 16pt;
          margin-bottom: 5px;
        }
        
        .fecha {
          text-align: center;
          font-size: 9pt;
          margin-bottom: 20px;
          color: #666;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9pt;
        }
        
        th {
          background: #000;
          color: white;
          padding: 8px 4px;
          text-align: left;
        }
        
        td {
          padding: 6px 4px;
          border-bottom: 1px solid #ccc;
        }
        
        .activo { background: #d4edda; }
        .inactivo { background: #f8d7da; }
        
        .total-row {
          font-weight: bold;
          background: #e9ecef;
        }
      </style>
    </head>
    <body>
      <h1>${empresa.nombre}</h1>
      <div class="fecha">${titulo} - ${formatearFechaReporte(fechaActual.toISOString())}</div>
      
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>NOMBRE</th>
            <th>DESCRIPCIÓN</th>
            <th>MARGEN</th>
            <th>PRODUCTOS</th>
            <th>ESTADO</th>
            <th>FECHA</th>
          </tr>
        </thead>
        <tbody>
          ${categorias.map(cat => `
            <tr class="${cat.estado?.toLowerCase() === 'activo' ? 'activo' : 'inactivo'}">
              <td>${cat.idCategoria || ''}</td>
              <td><strong>${cat.nombre || ''}</strong></td>
              <td>${cat.descripcion || '—'}</td>
              <td align="right">${formatearPorcentaje(cat.margen_ganancia_default)}</td>
              <td align="center">${cat.totalProductos || 0}</td>
              <td>${convertirEstadoTexto(cat.estado || '')}</td>
              <td>${formatearFechaCorta(cat.fecha_creacion || cat.fechaCreacion)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="7" align="right">
              Total Categorías: ${categorias.length} | 
              Activas: ${categorias.filter(c => c.estado?.toLowerCase() === 'activo').length} | 
              Inactivas: ${categorias.filter(c => c.estado?.toLowerCase() === 'inactivo').length}
            </td>
          </tr>
        </tfoot>
      </table>
      
      <div style="text-align: center; margin-top: 20px; font-size: 8pt;">
        Sistema de Gestión ${empresa.nombre} - Reporte generado automáticamente
      </div>
      
      <div class="no-print" style="text-align: center; margin-top: 20px;">
        <button onclick="window.print()" style="padding: 10px 30px; background: black; color: white; border: none; border-radius: 5px; cursor: pointer;">
          IMPRIMIR
        </button>
        <button onclick="window.close()" style="padding: 10px 30px; background: #666; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
          CERRAR
        </button>
      </div>
    </body>
    </html>
  `;
};

/**
 * Imprimir reporte simplificado
 */
export const imprimirReporteSimplificado = (categorias, titulo = 'LISTADO DE CATEGORÍAS') => {
  if (!categorias || categorias.length === 0) {
    alert('No hay categorías para mostrar');
    return;
  }
  
  const htmlReporte = generarHTMLReporteSimplificado(categorias, titulo);
  
  const ventana = window.open('', '_blank', 'width=1000,height=700');
  
  if (!ventana) {
    alert('Permite ventanas emergentes para imprimir');
    return;
  }
  
  ventana.document.write(htmlReporte);
  ventana.document.close();
};

export default {
  JEAN_INFORMATICO_CONFIG,
  formatearFechaReporte,
  formatearFechaCorta,
  formatearPorcentaje,
  convertirEstadoTexto,
  getColorEstado,
  calcularEstadisticasReporte,
  generarHTMLReporteCategorias,
  imprimirReporteCategorias,
  generarHTMLReporteSimplificado,
  imprimirReporteSimplificado
};